import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui-kit/dialog';
import { AddSquareIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft, ArrowRight, FileType2, Loader2, Pencil, Sparkles, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/state/store/auth';
import { useCreateTemplateRequest } from '@/modules/templates/hooks/use-templates';

interface NewTemplateDialogProps {
  orgSlug: string;
  orgName: string;
  orgId: string;
}

type Step = 'who' | 'diy' | 'selise';

function ChoiceCard({
  icon,
  iconActive,
  title,
  description,
  badge,
  badgeSecondary,
  onClick,
  busy,
}: {
  icon: React.ReactNode;
  iconActive?: boolean;
  title: string;
  description: string;
  badge?: string;
  badgeSecondary?: string;
  onClick: () => void;
  busy?: boolean;
}) {
  return (
    <Card
      role="button"
      tabIndex={0}
      className="cursor-pointer hover:border-selise-blue transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start gap-3 sm:gap-4">
          <div
            className={`h-10 w-10 sm:h-12 sm:w-12 rounded-xl flex items-center justify-center shrink-0 ${
              iconActive ? 'bg-selise-blue/10 text-selise-blue' : 'bg-muted text-globe-grey'
            }`}
          >
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold text-fg">{title}</h3>
              {busy ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-selise-blue" />
              ) : (
                <ArrowRight className="h-4 w-4 shrink-0 text-globe-grey group-hover:text-selise-blue transition-colors" />
              )}
            </div>
            <p className="text-sm text-globe-grey mt-1">{description}</p>
            {(badge || badgeSecondary) && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {badge && (
                  <span className="text-xs bg-selise-blue/10 text-selise-blue px-2 py-0.5 rounded-full">
                    {badge}
                  </span>
                )}
                {badgeSecondary && (
                  <span className="text-xs text-globe-grey">{badgeSecondary}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function NewTemplateDialog({ orgSlug, orgName, orgId }: NewTemplateDialogProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('who');
  const user = useAuthStore((state) => state.user);
  const createRequestMutation = useCreateTemplateRequest();

  const reset = () => setStep('who');

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  const handleRequestTemplate = async () => {
    try {
      await createRequestMutation.mutateAsync({
        OrganizationId: orgId,
        RequesterEmail: user?.email ?? '',
        RequesterName: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
        Type: 'collaboration',
        Notes: `Organization: ${orgName}`,
      });
      handleOpenChange(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title:
          error instanceof Error ? error.message : t('TEMPLATES_NEWTEMPLATEDIALOG_REQUESTERROR'),
      });
    }
  };

  const handleCreateSelf = () => {
    handleOpenChange(false);
    navigate(`/org/${orgSlug}/templates/new`);
  };
  const goToWordUpload = () => {
    handleOpenChange(false);
    navigate(`/org/${orgSlug}/templates/new/docx`);
  };

  const headerCopy: Record<Step, { title: string; description: string }> = {
    who: {
      title: t('TEMPLATES_NEWTEMPLATEDIALOG_TITLE'),
      description: t('TEMPLATES_NEWTEMPLATEDIALOG_DESCRIPTION'),
    },
    diy: {
      title: t('TEMPLATES_NEWTEMPLATEDIALOG_DIYTITLE'),
      description: t('TEMPLATES_NEWTEMPLATEDIALOG_DIYDESCRIPTION'),
    },
    selise: {
      title: t('TEMPLATES_NEWTEMPLATEDIALOG_SELISETITLE'),
      description: t('TEMPLATES_NEWTEMPLATEDIALOG_SELISEDESCRIPTION', { orgName }),
    },
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="primary"
          size="sm"
          aria-label={t('TEMPLATES_NEWTEMPLATEDIALOG_TRIGGERLABEL')}
        >
          <HugeiconsIcon icon={AddSquareIcon} size={20} />
          <span>{t('TEMPLATES_NEWTEMPLATEDIALOG_TRIGGERLABEL')}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white w-[95vw] max-w-lg max-h-[90dvh] p-4 sm:p-6 rounded-2xl flex flex-col gap-4 z-9999">
        <DialogHeader className="pr-8 text-left">
          {step !== 'who' && (
            <button
              type="button"
              onClick={() => setStep('who')}
              className="flex items-center gap-1 text-xs text-globe-grey hover:text-fg mb-1 w-fit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue rounded"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t('TEMPLATES_NEWTEMPLATEDIALOG_BACK')}
            </button>
          )}
          <DialogTitle className="text-lg sm:text-xl">{headerCopy[step].title}</DialogTitle>
          <DialogDescription>{headerCopy[step].description}</DialogDescription>
        </DialogHeader>

        {/* Step 1 — WHO builds it */}
        {step === 'who' && (
          <div className="flex-1 min-h-0 overflow-y-auto -mr-2 pr-2 grid gap-3 sm:gap-4">
            <ChoiceCard
              iconActive
              icon={<Users className="h-5 w-5 sm:h-6 sm:w-6" />}
              title={t('TEMPLATES_NEWTEMPLATEDIALOG_REQUESTTITLE')}
              description={t('TEMPLATES_NEWTEMPLATEDIALOG_REQUESTDESCRIPTION', { orgName })}
              badge={t('TEMPLATES_NEWTEMPLATEDIALOG_REQUESTRECOMMENDED')}
              badgeSecondary={t('TEMPLATES_NEWTEMPLATEDIALOG_REQUESTQUALITY')}
              onClick={() => setStep('selise')}
            />
            <ChoiceCard
              icon={<Pencil className="h-5 w-5 sm:h-6 sm:w-6" />}
              title={t('TEMPLATES_NEWTEMPLATEDIALOG_SELFTITLE')}
              description={t('TEMPLATES_NEWTEMPLATEDIALOG_SELFDESCRIPTION')}
              badgeSecondary={t('TEMPLATES_NEWTEMPLATEDIALOG_SELFBADGE')}
              onClick={() => setStep('diy')}
            />
          </div>
        )}

        {/* Step 2 — HOW (self-serve) */}
        {step === 'diy' && (
          <div className="flex-1 min-h-0 overflow-y-auto -mr-2 pr-2 grid gap-3 sm:gap-4">
            <ChoiceCard
              iconActive
              icon={<FileType2 className="h-5 w-5 sm:h-6 sm:w-6" />}
              title={t('TEMPLATES_NEWTEMPLATEDIALOG_WORDDOCTITLE')}
              description={t('TEMPLATES_NEWTEMPLATEDIALOG_WORDDOCDESCRIPTION')}
              onClick={goToWordUpload}
            />
            <ChoiceCard
              iconActive
              icon={<Sparkles className="h-5 w-5 sm:h-6 sm:w-6" />}
              title={t('TEMPLATES_NEWTEMPLATEDIALOG_AIBUILDERTITLE')}
              description={t('TEMPLATES_NEWTEMPLATEDIALOG_AIBUILDERDESCRIPTION')}
              onClick={handleCreateSelf}
            />
          </div>
        )}

        {/* SELISE confirm — deliberate action before submitting request */}
        {step === 'selise' && (
          <div className="grid gap-4">
            <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-globe-grey">
              {t('TEMPLATES_NEWTEMPLATEDIALOG_SELISECONFIRM', { orgName })}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep('who')}
                disabled={createRequestMutation.isPending}
              >
                {t('TEMPLATES_NEWTEMPLATEDIALOG_CANCEL')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleRequestTemplate}
                disabled={createRequestMutation.isPending}
              >
                {createRequestMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {t('TEMPLATES_NEWTEMPLATEDIALOG_SENDREQUEST')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
